import React, { useRef, useEffect, useState } from 'react';
import get from 'lodash/get';
import classNames from 'classnames/bind';
import { bool } from 'prop-types';
import NavigationHelper from 'yoda-core-components/lib/helpers/NavigationHelper/NavigationHelper';
import { dt } from 'yoda-core-components/lib/helpers/Utils/GetTailwindToken';
import { useSelector } from 'react-redux';
import Button from 'yoda-core-components/lib/components/Button';
import { getDeviceType as getDeviceTypeSelector } from '../../selectors/CMPageSelector';
import CMImageRenderer from '../CMImageRenderer/CMImageRenderer';
import { metaDataElement, metaDataProperty } from '../../utils/cmPreviewMetaData';
import {
    commonAutomationIds,
    heroAutomationIds,
    bannerAutomationIds,
} from '../CoreMediaPage/CMPage.config';
import { cmsCompHeadlineStyle, cmsCompSubHeadlineStyle } from '../../helpers/CMSUtils/cmsStyles';
import * as styles from './CMHero5Styles.css';

const cx = classNames.bind(styles);

const renderSecondaryImages = (data, componentType = 'hero5', dataStyles, props) => {
    const ctaLabelColors = `#${get(dataStyles, 'itemCTALabelColor', '')}`;
    const imagesDom = [];
    data?.forEach((item) => {
        const renderImage = (
            <CMImageRenderer
                imageURL={item?.media[0]?.uriTemplate || ''}
                altText={item?.media[0]?.alt || ''}
                componentType={componentType}
                imageAspectRatio="portrait_ratio1x1"
                imageType="secondary"
                isCMPreview={props?.isCMPreview}
                data={props?.data}
                automationId={bannerAutomationIds?.IMAGE}
            />
        );
        const banner = (
            <a
                className={`${cx('secondaryImagesList')} ${dt([
                    'md:w-[calc(50%-8px)]',
                    'smOnly:w-full',
                    'md:odd:mr-2',
                    'md:even:ml-2',
                    'smOnly:p-0',
                    'smOnly:mb-4',
                    'smOnly:block',
                ])}`}
                role="link"
                aria-label="hero5"
                data-automation-id={bannerAutomationIds?.LINK}
                href={item?.urlHolder?.value}
                target={item?.urlHolder?.openInNewTab ? '_blank' : ''}
                title={item?.altText || ''}
                rel="noreferrer"
            >
                <div className={dt(['mb-2'])}>{renderImage}</div>
                <div
                    className={dt([
                        'flex',
                        'justify-center',
                        'text-center',
                        'w-full',
                        'md:text-xLg',
                        'md:leading-[1.6]',
                        'smOnly:text-sm',
                        'smOnly:leading-5',
                        'smOnly:mb-2',
                        'font-open-sans-semibold',
                        'md:h-8',
                        'overflow-hidden',
                        'px-2',
                    ])}
                    style={{ color: ctaLabelColors }}
                    data-automation-id={bannerAutomationIds?.CTATEXT}
                >
                    {item?.ctaLabel}
                </div>
            </a>
        );
        imagesDom.push(banner);
    });
    return imagesDom;
};

/* istanbul ignore next */
const navigateToLinks = (event, url, dataItem) => {
    event.preventDefault();
    const openLinkInNewTab = get(dataItem, 'openInNewTab', '');
    return !__SERVER__ ? NavigationHelper.navigate(url, openLinkInNewTab) : '';
};

const renderPrimaryImage = (imageData, componentType = 'hero5', props) => (
    <CMImageRenderer
        imageURL={imageData?.media[0]?.uriTemplate || ''}
        altText={imageData?.media[0]?.alt || ''}
        componentType={componentType}
        imageAspectRatio="portrait_ratio5x7"
        imageType="primary"
        isCMPreview={props?.isCMPreview}
        data={props?.data}
        automationId={bannerAutomationIds?.IMAGE}
    />
);
const Hero5 = (props) => {
    const data = get(props, 'data', props);
    const styles = get(data, 'styles', {});
    const automationId = get(data, 'automationId', '');
    const { isCMPreview } = props;
    const deviceType = useSelector((state) => getDeviceTypeSelector(state));
    const primaryImage = get(data, 'items[0]', {});
    const secondaryImages = Array.isArray(data?.items) ? data?.items.slice(1, 5) : [];
    const [primaryImageWidthHeight, setPrimaryImageWidthHeight] = useState({});
    const primaryImageDivRef = useRef(null);
    useEffect(() => {
        const { clientHeight, clientWidth } = primaryImageDivRef?.current;
        setPrimaryImageWidthHeight({ height: clientHeight + 7, width: clientWidth + 17 });
    }, [primaryImageDivRef]);

    useEffect(() => {
        window?.addEventListener('resize', () => {
            const { clientHeight, clientWidth } = primaryImageDivRef?.current;
            /* istanbul ignore next */
            setPrimaryImageWidthHeight({ height: clientHeight + 7, width: clientWidth + 17 });
            renderSecondaryImages();
        });
        return () => window?.removeEventListener('resize', renderSecondaryImages());
    }, []);

    const componentBackgroundColor = `#${get(data, 'styles.componentBackgroundColor', '')}`;
    const componentHeadlineColor = `#${get(data, 'styles.componentHeadlineColor', '')}`;
    const componentSubHeadlineColor = `#${get(data, 'styles.componentSubHeadlineColor', '')}`;
    const itemCTALabelColor = `#${get(data, 'styles.itemCTALabelColor', '')}`;
    const hero5ComponentHeadlineFontSize =
        cmsCompHeadlineStyle[styles?.componentHeadlineFontSize] || '';
    const componentContainerBgColor =
        componentBackgroundColor === '#' ||
        componentBackgroundColor === '#None' ||
        componentBackgroundColor === '#ffffff';
    const secondaryImageContainer = [
        'flex',
        'flex-wrap',
        'smOnly:overflow-y-scroll',
        'smOnly:mb-6',
    ];
    const hero5ComponentSubHeadlineFontSize =
        cmsCompSubHeadlineStyle[styles?.componentSubHeadlineFontSize] || '';
    return (
        <div
            data-cm-metadata={isCMPreview && metaDataElement(data)}
            data-automation-id={automationId}
        >
            <div
                className={dt([
                    'relative',
                    'block',
                    componentContainerBgColor ? '' : 'lg:p-4',
                    componentContainerBgColor ? '' : 'smMd:p-6',
                    componentContainerBgColor ? '' : 'smMd:w-[calc(100%+48px)]',
                    componentContainerBgColor ? '' : 'smMd:-mx-6',
                    componentContainerBgColor ? 'smMd:pt-0' : 'smMd:pt-6',
                    'smOnly:px-6',
                    'smOnly:!pb-0',
                    'smOnly:my-0',
                    'smOnly:-mx-6',
                    'smOnly:w-[calc(100%+48px)]',
                    'md:w-full',
                ])}
                style={{ backgroundColor: componentBackgroundColor }}
            >
                <div
                    className={dt([
                        'flex',
                        'flex-col',
                        'lg:absolute',
                        'lg:top-0',
                        'lg:left-0',
                        componentContainerBgColor ? 'lg:py-10' : 'lg:py-14',
                        componentContainerBgColor ? 'lg:pl-10' : 'lg:pl-14',
                        'smMd:text-center',
                        'sm:z-[1]',
                        'smMd:w-full',
                        !componentContainerBgColor ? 'lg:w-[42%]' : 'lg:w-[43%]',
                        'pointer-events-none',
                    ])}
                >
                    {data?.teaserTitle ? (
                        <div
                            className={dt([
                                'font-open-sans-bold',
                                'pb-1',
                                'smOnly:!pb-0',
                                componentContainerBgColor
                                    ? 'lg:max-w-[calc(67%+40px)]'
                                    : 'lg:max-w-[calc(67%+56px)]',
                                hero5ComponentHeadlineFontSize,
                            ])}
                            id="hero5Title"
                            style={{ color: componentHeadlineColor }}
                            data-cm-metadata={isCMPreview && metaDataProperty('teaserTitle')}
                            data-automation-id={commonAutomationIds?.MAINTITLE}
                        >
                            {data?.teaserTitle}
                        </div>
                    ) : null}
                    {data?.teaserSubtitle ? (
                        <h4
                            className={`${dt([
                                'font-open-sans-regular',
                                'pb-2',
                                'smOnly:!pb-1',
                                componentContainerBgColor
                                    ? 'lg:max-w-[calc(67%+40px)]'
                                    : 'lg:max-w-[calc(67%+56px)]',
                            ])} ${hero5ComponentSubHeadlineFontSize}`}
                            id="hero5SubTitle"
                            style={{ color: componentSubHeadlineColor }}
                            data-cm-metadata={isCMPreview && metaDataProperty('teaserSubtitle')}
                            data-automation-id={commonAutomationIds?.MAINSUBTITLE}
                        >
                            {data?.teaserSubtitle}
                        </h4>
                    ) : null}

                    {data?.legalText?.legalText ? (
                        <div
                            className={dt([
                                'flex',
                                'w-full',
                                'smMd:justify-center',
                                'smMd:self-center',
                                'text-xsmall',
                                'font-normal',
                                'min-h-[16px]',
                                'pb-4',
                            ])}
                            style={{ color: componentSubHeadlineColor }}
                            data-cm-metadata={
                                isCMPreview && metaDataProperty('legalText.legalText')
                            }
                            data-automation-id={commonAutomationIds?.LEGALTEXT}
                        >
                            <p
                                className={dt([
                                    'text-xsmall',
                                    'flex',
                                    'flex-wrap',
                                    'smMd:justify-center',
                                    'smMd:items-center',
                                    'lg:inline',
                                ])}
                            >
                                {data.legalText.legalText}
                                <Button
                                    automationId={commonAutomationIds?.LEGALLINK}
                                    linkUrl={
                                        data?.legalText?.urlHolder
                                            ? data.legalText.urlHolder?.value
                                            : ''
                                    }
                                    id="hero5LegalText"
                                    target={
                                        data?.legalText?.urlHolder?.openInNewTab ? '_blank' : ''
                                    }
                                    onClick={(event) => {
                                        navigateToLinks(
                                            event,
                                            data.legalText.urlHolder?.value,
                                            data?.legalText?.urlHolder
                                        );
                                    }}
                                    title={data?.legalText?.legalAltText || ''}
                                    type="button"
                                    buttonType="Text"
                                    style={{ color: componentSubHeadlineColor }}
                                    className={dt([
                                        'lg:inline',
                                        'w-auto',
                                        'font-normal',
                                        'smMd:px-1',
                                        'pointer-events-auto',
                                    ])}
                                >
                                    {data.legalText.legalLinkText}
                                </Button>
                            </p>
                        </div>
                    ) : null}
                </div>
                <div className={dt(['flex', 'w-full', 'flex-wrap', 'items-stretch'])}>
                    <div
                        className={dt([
                            'lg:w-[44.4%]',
                            'mdOnly:w-[46.1%]',
                            'smOnly:w-[68.2%]',
                            'pr-4',
                            'overflow-hidden',
                        ])}
                        data-automation-id={heroAutomationIds?.PRIMARYCONTAINER}
                    >
                        <div
                            className={dt([
                                'relative',
                                'w-full',
                                'md:h-full',
                                'smOnly:pb-[157.21%]',
                                'smOnly:h-0',
                                'smOnly:pb-[157.21%]',
                            ])}
                            ref={primaryImageDivRef}
                        >
                            <div
                                className={dt([
                                    'smOnly:absolute',
                                    'smOnly:top-0',
                                    'smOnly:left-0',
                                    'w-full',
                                    'h-full',
                                ])}
                            >
                                <div className={dt(['h-full'])}>
                                    <a
                                        className={dt(['block'])}
                                        role="link"
                                        aria-label="hero5"
                                        data-automation-id={bannerAutomationIds?.LINK}
                                        href={data?.items[0]?.urlHolder?.value}
                                        onClick={(event) => {
                                            navigateToLinks(
                                                event,
                                                data?.items[0]?.urlHolder?.value,
                                                data?.items[0]?.urlHolder
                                            );
                                        }}
                                        target={
                                            data?.items[0]?.urlHolder?.openInNewTab ? '_blank' : ''
                                        }
                                        title={data?.items[0]?.altText || ''}
                                        rel="noreferrer"
                                    >
                                        <div className={dt(['mb-2'])}>
                                            {renderPrimaryImage(
                                                primaryImage,
                                                data?.componentType,
                                                props
                                            )}
                                        </div>
                                        <div
                                            className={dt([
                                                'flex',
                                                'justify-center',
                                                'text-center',
                                                'w-full',
                                                'mdOnly:h-8',
                                                'md:text-xLg',
                                                'md:leading-[1.6]',
                                                'smOnly:text-small',
                                                'font-open-sans-semibold',
                                                'smOnly:mb-2',
                                                'px-2',
                                            ])}
                                            style={{ color: itemCTALabelColor }}
                                            data-automation-id={bannerAutomationIds?.CTATEXT}
                                        >
                                            {data?.items[0].ctaLabel}
                                        </div>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div
                        className={`${cx('hideScroll')} ${dt([...secondaryImageContainer])}`}
                        style={{
                            height: `${
                                deviceType.isMobile
                                    ? `${primaryImageWidthHeight?.height}px`
                                    : 'auto'
                            }`,
                            width: `calc(100% - ${primaryImageWidthHeight?.width}px)`,
                        }}
                        data-automation-id={heroAutomationIds?.SECONDARYCONTAINER}
                    >
                        <div className={dt(['relative', 'w-full', 'h-full'])}>
                            <div className={dt(['w-full', 'h-full'])}>
                                <div
                                    className={dt([
                                        'h-full',
                                        'flex',
                                        'flex-wrap',
                                        'content-between',
                                    ])}
                                >
                                    {!deviceType.isMobile ? (
                                        renderSecondaryImages(
                                            secondaryImages,
                                            data?.componentType,
                                            data?.styles,
                                            props
                                        )
                                    ) : (
                                        <div
                                            className={cx('mblLandscape')}
                                            style={{ color: itemCTALabelColor }}
                                        >
                                            {renderSecondaryImages(
                                                secondaryImages,
                                                data?.componentType,
                                                null,
                                                props
                                            )}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

Hero5.defaultProps = {
    isCMPreview: false,
};

Hero5.propTypes = {
    isCMPreview: bool,
};

export default Hero5;
